import { pick } from 'lodash';
import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';

import { categories, statuses } from '../../../common/threads';
import { getContentLanguagesValues, getCurrentInterfaceLanguage } from '../../../contexts/LanguageContext';
import Button from '../../Atoms/Button';
import ButtonsContainer from '../../Atoms/ButtonsContainer';
import FormGrid from '../../Atoms/FormGrid';
import FormGroup from '../../Atoms/FormGroup';
import Input from '../../Atoms/Input';
import Select from '../../Atoms/Select';
import Switch from '../../Atoms/Switch';
import Editor from '../../BBcode/Editor';

const POSITION_BUTTON = 'RIGHT';

const filterCategories = (allowedActions, mode) => (e) => {
  if (e.id === 'ALL' || e.id === 'REPLIED') {
    return false;
  }

  // If we are allowed to create all kind of threads, we remove the "Welcome" category to avoid mistakes
  if (e.id === 'WELCOME' && mode === 'CREATE' && allowedActions.includes('CREATE_THREAD')) {
    return false;
  }

  if (e.id === 'WELCOME' && allowedActions.includes('CREATE_INTRODUCTION_THREAD')) {
    return true;
  }

  if ((!e.private || (e.private === true && allowedActions.includes('ACCESS_PRIVATE_THREADS'))) && (allowedActions.includes('CREATE_THREAD') || mode === 'EDIT')) {
    return true;
  }
};

const ThreadForm = ({
  t,
  thread = { language: getCurrentInterfaceLanguage(), title: '', isPinned: false, requiredRole: 'GUEST', isLocked: false, message: '' },
  onSubmit = () => { },
  loading = false,
  buttonLabel,
  allowedActions = [],
  mode = 'CREATE',
  username = '',
  useIntroductionMode = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const categoryList = categories.filter(filterCategories(allowedActions, mode)).map((e) => ({ value: e.id, label: e.title(t) }));
  const requiredRoleList = statuses.map((e) => ({ value: e.id, label: e.title(t) }));
  const form = useForm({
    mode: 'all',
    defaultValues: {
      ...pick(thread, ['category', 'language', 'title', 'isPinned', 'isLocked', 'message', 'requiredRole']),
      ...(thread.category ? { category: thread.category } : { category: categoryList?.[0]?.value }),
      ...(useIntroductionMode ? { title: `#${username}` } : {}),
      message: thread?.messages?.edges?.[0]?.node?.message?.value || '',
    },
  });

  const editorRef = useRef(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
  } = form;

  const required = t('Required field');

  const validatorForm = {
    title: register('title', { required, minLength: { value: 3, message: t('The title need to be at least 3 characters long') } }),
    category: register('category', { required }),
    language: register('language', { required }),
    isPinned: register('isPinned'),
    requiredRole: register('requiredRole', { required }),
    isLocked: register('isLocked'),
    ...(mode === 'CREATE' ? { message: register('message', { required }) } : {}),
  };

  return (
    <FormProvider {...form}>
      <form id="thread">
        <FormGrid>
          <FormGroup label={t('Title')} id="title" error={errors.title} required>
            <Input id="title" register={validatorForm.title} readOnly={useIntroductionMode} />
          </FormGroup>
          <FormGroup label={t('Language')} id="language" error={errors.language} required>
            <Select control={control} options={getContentLanguagesValues()} register={validatorForm.language} />
          </FormGroup>
          {!useIntroductionMode && <FormGroup label={t('Category')} id="category" error={errors.category} required>
            <Select control={control} options={categoryList} register={validatorForm.category} />
          </FormGroup>}

          {allowedActions.includes('CREATE_THREAD_ROLE') && (
            <FormGroup label={t('Visibility')} id="requiredRole" error={errors.requiredRole} required>
              <Select control={control} options={requiredRoleList} register={validatorForm.requiredRole} />
            </FormGroup>
          )}
          {allowedActions.includes('CREATE_THREAD_PINNED') && (
            <FormGroup label={t('Is pinned?')} id="isPinned" error={errors.isPinned}>
              <Switch id="isPinned" register={validatorForm.isPinned} />
            </FormGroup>
          )}
          {allowedActions.includes('CREATE_THREAD_LOCKED') && (
            <FormGroup label={t('Is locked?')} id="isLocked" error={errors.isLocked}>
              <Switch id="isLocked" register={validatorForm.isLocked} />
            </FormGroup>
          )}
        </FormGrid>

        {mode === 'CREATE' && (
          <FormGroup label={useIntroductionMode ? t('Your introduction') : t('Message')} error={errors.message} required>
            <Editor
              ref={editorRef}
              control={control}
              placeholder={useIntroductionMode ? t('- What are your hobbies in real life?\n- How old are you?\n- Have you already done some brickfilms?\n- What software are you using?\n- How did you discover Brickfilms.com?\n- What do you expect from the community?') : t('Write your message')}
              t={t}
              register={validatorForm.message}
              onUploadStatusChange={setIsUploading}
            />
          </FormGroup>
        )}

        <ButtonsContainer position={POSITION_BUTTON}>
          <Button action={handleSubmit(onSubmit)} disabled={!isDirty || !isValid || isUploading} loading={loading} type="submit" form="thread" label={buttonLabel} />
        </ButtonsContainer>
      </form>
    </FormProvider>
  );
};

export default withTranslation()(ThreadForm);
